import {
  IAllTagsQuery,
  IBlocksByTagsQuery,
  IBlocksByTagsResult,
  INotesByTagsQuery,
  INotesByTagsResult,
  OverrideSQuery,
  OverrideSResult,
} from '@rhizome/pgtypes';
import Link from 'next/link';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useFetcher, usePaginatedQuery } from '../utils/DataFetchers';
import { safeNanoid } from '../utils/safeNanoid';
import { useAuthor } from '../utils/useAuthor';
import ExcerptDisplay from './ExcerptDisplay';
import SidebarNote from './NotesLayout/SidebarNote';
import { ErrorDisplay } from './StatusUI/ErrorDisplay';
import { LoadingPulser } from './StatusUI/LoadingPulser';
import TagDisplay from './TagDisplay';

export enum Mode {
  Excerpts,
  Notes,
}

function BacklinksWrapper(props: {
  // a list of initially selected tags
  initialTags?: string[] | null;
  containerRef: React.RefObject<any>;
  public: boolean;
  open: boolean;
  initialExcerpts?: OverrideSResult<IBlocksByTagsResult>[];
  initialNotes?: INotesByTagsResult[];
  isRoot: boolean;
}) {
  // STATE
  const [selectedTags, setSelectedTags] = useState(
    props.initialTags ? new Set(props.initialTags) : new Set<string>()
  );
  const [mode, setMode] = useState(Mode.Excerpts);

  const author = useAuthor((state) => state.author);

  const {
    data: excerpts,
    status: excerptsStatus,
    fetchNextPage: fetchMoreExcerpts,
    isFetchingNextPage: fetchingMoreExcerpts,
    containerRef,
  } = usePaginatedQuery<OverrideSQuery<IBlocksByTagsQuery>>(
    [
      'BlocksByTags',
      {
        tags: Array.from(selectedTags),
        public: props.public ? true : null,
        creation_date: null,
        note_author: author || null,
        limit: true,
      },
    ],
    {
      paginateOn: 'creation_date',
      existingRef: props.containerRef,
      initialData: props.initialExcerpts,
    }
  );

  const { data: notes, status: notesStatus } =
    usePaginatedQuery<INotesByTagsQuery>(
      [
        'NotesByTags',
        {
          tags: Array.from(selectedTags),
          public: props.public ? true : null,
          creation_date: null,
          author: author || null,
          limit: true,
        },
      ],
      {
        paginateOn: 'creation_date',
        existingRef: props.containerRef,
        initialData: props.initialNotes,
      }
    );

  const { data: allTags, status: tagStatus } = useFetcher<IAllTagsQuery>(
    [
      'AllTags',
      {
        tags: Array.from(selectedTags),
        public: props.public,
        author: author || null,
      },
    ],

    { keepPreviousData: true }
  );

  // need to merge blocks

  const backlinkId = useRef(safeNanoid());
  return (
    <div style={{ wordBreak: 'break-word' }}>
      <StickyWrapper>
        {allTags && allTags.length > 0 ? (
          <TagDisplay
            tags={allTags}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            open={props.open}
          />
        ) : tagStatus === 'loading' ? null : (
          <>
            <div>No tags to display - tag some text, then come back later.</div>
            <p>
              {' '}
              {'('}What\&apos;s a tag? Find out{' '}
              <Link href="/notes/help/basics">here</Link>
              {')'}
            </p>
          </>
        )}
        {selectedTags.size > 0 ? (
          <>
            {' '}
            show:{' '}
            <ContentSelector
              onClick={() => {
                setMode(Mode.Excerpts);
              }}
              selected={mode === Mode.Excerpts}
            >
              excerpts
            </ContentSelector>{' '}
            <ContentSelector
              selected={mode === Mode.Notes}
              onClick={() => {
                setMode(Mode.Notes);
              }}
            >
              notes
            </ContentSelector>
          </>
        ) : (
          <NoTagMessage>no tags selected</NoTagMessage>
        )}
        {selectedTags.size > 0 ? (
          mode === Mode.Notes ? (
            <div>
              {notesStatus === 'loading' ? (
                <div style={{ height: '150px' }}>
                  <LoadingPulser />
                </div>
              ) : null}
              {notesStatus === 'error' ? <ErrorDisplay /> : null}
              {notes?.pages?.map((page) => {
                return page?.map((note) => {
                  return (
                    <SidebarNote
                      public={props.public}
                      key={note.note_id}
                      noteId={note.note_id}
                      title={note.title || ''}
                      creationDate={note.creation_date}
                      lastEditedDate={note.last_edited_date}
                    />
                  );
                });
              })}
            </div>
          ) : (
            <div ref={containerRef}>
              {excerptsStatus === 'loading' ? (
                <div style={{ height: '150px' }}>
                  <LoadingPulser />
                </div>
              ) : null}
              {excerptsStatus === 'error' ? <ErrorDisplay /> : null}
              {excerpts?.pages?.map((page, pageNumber) => {
                return page?.map((excerpt, i) => {
                  const uniqueId =
                    backlinkId.current + excerpt.block_id + ' ' + i.toString();
                  return (
                    <ExcerptDisplay
                      key={uniqueId}
                      {...excerpt}
                      page={pageNumber}
                      isRoot={props.isRoot}
                    />
                  );
                });
              })}
            </div>
          )
        ) : null}
      </StickyWrapper>
    </div>
  );
}

const StickyWrapper = styled.div`
  /* position:sticky;
top:0px; */
`;

export const ContentSelector = styled.button<{ selected: boolean }>`
  border: none;
  vertical-align: text-top;
  /* padding: 5px; */
  border-radius: 0.2rem;
  background-color: ${(props) =>
    props.selected
      ? props.theme.colors.main.dark
      : props.theme.colors.main.light};
  font-size: 17px;

  :hover {
    background-color: ${(props) => props.theme.colors.main.dark};
  }
`;

export const ContentSelectorContainer = styled.div`
  width: 100%;
`;

export const NoTagMessage = styled.h3`
  padding: 20px 20px 100px 0px;
`;

export default BacklinksWrapper;
